import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { STORAGE_KEY } from '../i18n/config';

export type Language = 'ar' | 'en';

export function useLanguage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const language = (i18n.language?.split('-')[0] || 'ar') as Language;
  const isRtl = language === 'ar';

  const setLanguage = useCallback(
    (lng: Language) => {
      i18n.changeLanguage(lng);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lng;
        document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
        document.body.setAttribute('dir', lng === 'ar' ? 'rtl' : 'ltr');
        try {
          localStorage.setItem(STORAGE_KEY, lng);
        } catch {
          // ignore
        }
      }
    },
    [i18n],
  );

  const toggleLanguage = useCallback(() => {
    const next: Language = language === 'ar' ? 'en' : 'ar';
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts.length >= 2 && (parts[0] === 'en' || parts[0] === 'ar')) {
      const rest = parts.slice(1).join('/');
      navigate(`/${next}/${rest}${location.search}${location.hash}`, { replace: true });
    }
    setLanguage(next);
  }, [language, setLanguage, navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const lng = language;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lng;
      document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.body.setAttribute('dir', lng === 'ar' ? 'rtl' : 'ltr');
    }
  }, [language]);

  return { language, isRtl, setLanguage, toggleLanguage };
}
